import React, { useState, useEffect } from 'react';
import { AlertCircle, RefreshCw, TrendingUp, Clock } from 'lucide-react';
import billingService from '../../../services/billingService';
import toast from 'react-hot-toast';

const fmt = (n) => (n ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';

function OutstandingTable({ data, loading, title, emptyMsg }) {
  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-4 border-purple-600 border-t-transparent" /></div>;
  if (!data.length) return <div className="py-12 text-center text-slate-400 font-semibold">{emptyMsg}</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 border-b border-slate-100">
          <tr>
            <th className="px-4 py-2.5 text-left font-bold text-slate-500 uppercase">Party</th>
            <th className="px-4 py-2.5 text-right font-bold text-slate-500 uppercase">Billed</th>
            <th className="px-4 py-2.5 text-right font-bold text-slate-500 uppercase">Paid</th>
            <th className="px-4 py-2.5 text-right font-bold text-slate-500 uppercase">Outstanding</th>
            <th className="px-4 py-2.5 text-center font-bold text-slate-500 uppercase"># Invoices</th>
            <th className="px-4 py-2.5 text-center font-bold text-slate-500 uppercase hidden md:table-cell">Oldest</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-50">
          {data.map((row, i) => {
            const name = row._id?.partyName || '—';
            const gstin = row._id?.customerGstin || row._id?.vendorGstin || '';
            return (
              <tr key={i} className="hover:bg-slate-50/70">
                <td className="px-4 py-3">
                  <p className="font-bold text-slate-900">{name}</p>
                  {gstin && <p className="font-mono text-[10px] text-slate-400">{gstin}</p>}
                </td>
                <td className="px-4 py-3 text-right font-mono font-semibold text-slate-700">₹{fmt(row.totalBilled)}</td>
                <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-700">₹{fmt(row.totalPaid)}</td>
                <td className="px-4 py-3 text-right font-mono font-black text-red-600">₹{fmt(row.outstanding)}</td>
                <td className="px-4 py-3 text-center font-bold text-slate-700">{row.invoiceCount}</td>
                <td className="px-4 py-3 text-center text-slate-500 hidden md:table-cell">{fmtDate(row.oldestInvoice)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot className="bg-slate-100 border-t-2 border-slate-300">
          <tr>
            <td className="px-4 py-2.5 font-black text-slate-900 text-xs uppercase">Total</td>
            <td className="px-4 py-2.5 text-right font-mono font-black text-slate-900 text-xs">₹{fmt(data.reduce((s, r) => s + r.totalBilled, 0))}</td>
            <td className="px-4 py-2.5 text-right font-mono font-black text-emerald-700 text-xs">₹{fmt(data.reduce((s, r) => s + r.totalPaid, 0))}</td>
            <td className="px-4 py-2.5 text-right font-mono font-black text-red-700 text-xs">₹{fmt(data.reduce((s, r) => s + r.outstanding, 0))}</td>
            <td colSpan={2} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export function OutstandingReceivables() {
  const [data, setData]     = useState([]);
  const [loading, setLoading] = useState(true);
  const fetch = async () => {
    setLoading(true);
    try { const res = await billingService.getOutstandingReceivables(); setData(res.data || []); }
    catch { toast.error('Failed to load receivables'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, []);
  const total = data.reduce((s, r) => s + r.outstanding, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-black text-slate-900">Outstanding Receivables</h1>
          <p className="text-xs text-slate-500 font-medium">Amounts pending from customers</p>
        </div>
        <button onClick={fetch} className="p-2 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-2xl px-5 py-4">
        <TrendingUp className="w-5 h-5 text-red-500" />
        <div>
          <p className="text-[10px] font-bold text-red-500 uppercase tracking-wider">Total Outstanding Receivable</p>
          <p className="text-2xl font-black text-red-700 font-mono">₹{fmt(total)}</p>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <OutstandingTable data={data} loading={loading} emptyMsg="No outstanding receivables! All customers are up to date." />
      </div>
    </div>
  );
}

export function OutstandingPayables() {
  const [data, setData]     = useState([]);
  const [loading, setLoading] = useState(true);
  const fetch = async () => {
    setLoading(true);
    try { const res = await billingService.getOutstandingPayables(); setData(res.data || []); }
    catch { toast.error('Failed to load payables'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, []);
  const total = data.reduce((s, r) => s + r.outstanding, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-black text-slate-900">Outstanding Payables</h1>
          <p className="text-xs text-slate-500 font-medium">Amounts due to suppliers</p>
        </div>
        <button onClick={fetch} className="p-2 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4">
        <Clock className="w-5 h-5 text-amber-500" />
        <div>
          <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">Total Outstanding Payable</p>
          <p className="text-2xl font-black text-amber-700 font-mono">₹{fmt(total)}</p>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <OutstandingTable data={data} loading={loading} emptyMsg="No outstanding payables! All suppliers are paid up to date." />
      </div>
    </div>
  );
}
